import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldAlert, ShieldCheck, KeyRound, Copy } from "lucide-react";

/**
 * Reset2FA — self-service Reset 2FA flow for users whose authenticator app
 * has drifted out of sync with the server's stored TOTP secret.
 *
 * Backed by /api/auth/2fa/reset-{request,token-info,generate,confirm}.
 * Entry point: user clicks the emailed link (arrives on /reset-2fa?token=...).
 * Also serves as a bare-URL entry point that prompts for email+password to
 * kick off /reset-request.
 */
const API = process.env.REACT_APP_BACKEND_URL;

export default function Reset2FA() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  const [phase, setPhase] = useState(token ? "loading" : "request");
  const [emailMasked, setEmailMasked] = useState("");
  const [error, setError] = useState("");

  // Request-phase state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Generate-phase state
  const [qr, setQr] = useState(null);
  const [pendingSecret, setPendingSecret] = useState("");
  const [totpCode, setTotpCode] = useState("");

  // Success-phase state
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/auth/2fa/reset-token-info?token=${encodeURIComponent(token)}`,
        );
        if (!data.valid) {
          setError(`This link is no longer valid (${data.reason}).  Request a new one below.`);
          setPhase("request");
          return;
        }
        setEmailMasked(data.email_masked);
        setPhase("verify_identity");
      } catch (e) {
        setError("Could not validate reset link. Request a new one below.");
        setPhase("request");
      }
    })();
  }, [token]);

  async function submitRequest(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/auth/2fa/reset-request`, {
        email, password,
      });
      toast.success(data.message);
      setPhase("email_sent");
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to submit request. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function generateNewQR() {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/api/auth/2fa/reset-generate`, null,
        { headers: { "X-Reset-Token": token } },
      );
      setQr(data.qr);
      setPendingSecret(data.secret);
      setPhase("scan_qr");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not generate new QR. Link may have expired.");
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmCode(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/api/auth/2fa/reset-confirm`,
        { totp_code: totpCode.trim() },
        { headers: { "X-Reset-Token": token } },
      );
      setRecoveryCodes(data.recovery_codes || []);
      setPhase("success");
      toast.success("2FA reset complete");
    } catch (e) {
      setError(e.response?.data?.detail || "Code did not match. Rescan the QR and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-50 p-4"
      data-testid="reset-2fa-page"
    >
      <div className="w-full max-w-md bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-6">
          <ShieldAlert className="w-5 h-5 text-violet-700" />
          <div className="text-xs uppercase tracking-wider font-bold text-violet-700">
            ZidaMD · Security
          </div>
        </div>

        {phase === "loading" && (
          <div className="flex items-center gap-2 text-slate-600" data-testid="reset-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Validating reset link…
          </div>
        )}

        {error && (
          <div
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 mb-4"
            data-testid="reset-error"
          >
            {error}
          </div>
        )}

        {phase === "request" && (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">Reset your authenticator</h1>
            <p className="text-sm text-slate-600 mb-5">
              Use this only if your authenticator app is producing codes that keep being rejected
              (an orphaned secret from an abandoned enrollment).  You'll receive an email link
              to re-pair a new authenticator entry — your existing 2FA remains active until you
              scan the new QR.
            </p>
            <form onSubmit={submitRequest} className="space-y-3">
              <input
                type="email" required placeholder="Email address"
                value={email} onChange={e => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded"
                data-testid="reset-email-input"
              />
              <input
                type="password" required placeholder="Password"
                value={password} onChange={e => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded"
                data-testid="reset-password-input"
              />
              <button
                type="submit" disabled={submitting}
                className="w-full py-2 bg-violet-700 hover:bg-violet-800 text-white rounded font-semibold disabled:opacity-50"
                data-testid="reset-request-submit"
              >
                {submitting ? "Sending…" : "Send reset link"}
              </button>
            </form>
            <p className="mt-4 text-xs text-slate-500">
              For your safety, we'll respond identically whether or not the account exists.
              Check your registered email for the reset link if this account is valid.
            </p>
          </>
        )}

        {phase === "email_sent" && (
          <div data-testid="reset-email-sent">
            <ShieldCheck className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-slate-900 text-center mb-2">
              Check your email
            </h2>
            <p className="text-sm text-slate-600 text-center">
              If an account exists for that address, we've sent a single-use reset link that
              expires in 30 minutes.  Follow the link on this same device to continue.
            </p>
          </div>
        )}

        {phase === "verify_identity" && (
          <div data-testid="reset-verify-identity">
            <h1 className="text-2xl font-bold text-slate-900 mb-1">
              Reset 2FA for {emailMasked}
            </h1>
            <p className="text-sm text-slate-600 mb-5">
              You'll pair a new authenticator entry now.  Your existing 2FA stays active until
              you successfully scan the new QR — so if you close this page, nothing changes.
            </p>
            <button
              onClick={generateNewQR} disabled={submitting}
              className="w-full py-2 bg-violet-700 hover:bg-violet-800 text-white rounded font-semibold disabled:opacity-50"
              data-testid="reset-generate-qr"
            >
              {submitting ? "Generating…" : "Generate new QR code"}
            </button>
          </div>
        )}

        {phase === "scan_qr" && (
          <div data-testid="reset-scan-qr">
            <h2 className="text-lg font-bold text-slate-900 mb-3">Scan the QR</h2>
            <ol className="text-sm text-slate-600 space-y-1 mb-4 list-decimal ml-4">
              <li>Open your authenticator app.</li>
              <li>Delete any existing <strong>ZidaMD</strong> entries in your authenticator app.</li>
              <li>Add a new account → scan this QR.</li>
              <li>Enter the first 6-digit code below.</li>
            </ol>
            {qr && (
              <img
                src={qr} alt="Authenticator QR code" data-testid="reset-qr-image"
                className="mx-auto w-52 h-52 border border-slate-200 rounded"
              />
            )}
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer text-slate-500">Can't scan? Enter manually</summary>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 bg-slate-100 p-2 rounded font-mono text-[11px] break-all"
                      data-testid="reset-secret-plaintext">{pendingSecret}</code>
                <button onClick={() => { navigator.clipboard?.writeText(pendingSecret); toast.success("Copied"); }}
                        className="p-1.5 rounded hover:bg-slate-100"
                        data-testid="reset-secret-copy">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </details>
            <form onSubmit={confirmCode} className="mt-4 space-y-3">
              <input
                type="text" required inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                placeholder="6-digit code"
                value={totpCode} onChange={e => setTotpCode(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded text-center text-lg tracking-widest font-mono"
                data-testid="reset-totp-input"
              />
              <button
                type="submit" disabled={submitting || totpCode.length !== 6}
                className="w-full py-2 bg-violet-700 hover:bg-violet-800 text-white rounded font-semibold disabled:opacity-50"
                data-testid="reset-confirm-submit"
              >
                {submitting ? "Verifying…" : "Confirm and complete reset"}
              </button>
            </form>
          </div>
        )}

        {phase === "success" && (
          <div data-testid="reset-success">
            <ShieldCheck className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-slate-900 text-center mb-2">
              2FA reset complete
            </h2>
            <p className="text-sm text-slate-600 mb-4">
              Your new authenticator is now paired.  Save these <strong>{recoveryCodes.length}</strong> recovery
              codes offline — they are shown ONCE.  Each unlocks your account exactly once if you
              lose your device.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <KeyRound className="w-4 h-4 text-amber-700" />
                <div className="text-xs font-bold text-amber-900 uppercase tracking-wider">
                  Recovery codes (copy now)
                </div>
              </div>
              <pre
                className="font-mono text-sm text-slate-800 whitespace-pre-wrap break-all"
                data-testid="reset-recovery-codes"
              >{recoveryCodes.join("\n")}</pre>
              <button
                onClick={() => { navigator.clipboard?.writeText(recoveryCodes.join("\n")); toast.success("Copied all codes"); }}
                className="mt-2 text-xs px-2 py-1 border border-amber-300 rounded hover:bg-amber-100"
                data-testid="reset-copy-all-recovery"
              >
                <Copy className="w-3 h-3 inline mr-1" /> Copy all
              </button>
            </div>
            <button
              onClick={() => nav("/login")}
              className="w-full py-2 bg-violet-700 hover:bg-violet-800 text-white rounded font-semibold"
              data-testid="reset-goto-login"
            >
              Go to login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
